import type { Locale } from '@360face/domain';

/**
 * Strings that exist only in kiosk v2 (owner 16.10.2026: two versions side by side to compare).
 * Everything else comes from the v1 kiosk messages, so both versions say the same things the same way.
 */
const pt = {
  detailsTitle: 'Quase pronto — só alguns dados',
  isThisYou: (name: string) => `É ${name}?`,
  knownSub: 'Encontrámos os seus dados pelo número de telemóvel.',
  knownEmailNeeded: 'Ainda não temos o seu e-mail. Escreva-o, por favor.',
  knownReady: 'Os seus dados estão prontos',
  homeCheckin: 'Tenho marcação na 360°FACE',
  homeCheckinSub: 'Faça check-in em 3 toques',
  homeBook: 'Marcar',
  homeBookSub: 'Hoje ou noutro dia',
  homeGift: 'Certificado de oferta + caixa de presente',
  homeGiftSub: '40–150 €',
  homeAbout: 'Saber mais sobre nós',
  homeAboutSub: 'Primeira vez aqui? O que fazemos, para quem, desde quanto · 1 min',
  aboutTitle: 'Lifting facial sem injeções',
  aboutLead:
    'Massagem profunda do rosto, pescoço e corpo. 30–65 minutos. O resultado vê-se logo à saída e dura — sem agulhas, sem tempo de recuperação.',
  aboutHero: 'Massagem facial — a nossa especialidade',
  aboutHeroSub: 'Contorno mais definido · menos inchaço · pele com luz · tensão do maxilar solta',
  aboutSolves: 'A massagem facial resolve',
  aboutSolvesList: [
    'Oval do rosto a perder definição, papada',
    'Inchaço e olheiras — drenagem linfática',
    'Tensão no maxilar, bruxismo, dores de cabeça',
    'Rugas de expressão, testa e «pé de galinha»',
    'Pele baça e cansada — fica com luz',
  ],
  aboutFace: 'Rosto',
  aboutFaceText: 'Face Workout, Korugi, Kobido, bucal. Lifting natural que treina os músculos do rosto.',
  aboutBody: 'Corpo',
  aboutBodyText: 'Massagem corporal para soltar as costas, as pernas e a cabeça. Sai mais leve.',
  aboutSkin: 'Pele',
  aboutSkinText: 'Limpeza profunda, peeling, LED, carboxiterapia. Pele limpa, calma, luminosa.',
  aboutFirst: 'Primeira vez?',
  aboutFirstText: 'Diga-nos o que a incomoda — a terapeuta escolhe a técnica.',
  aboutTry: 'Experimente',
  aboutFrom: 'desde',
  aboutStudios: 'estúdios · Parede e Lisboa',
  aboutMinutes: 'minutos por sessão',
  aboutZero: 'injeções, agulhas, recuperação',
  aboutWho: 'Quem somos',
  aboutWhoText:
    '360°FACE é um estúdio de lifting facial manual, em Parede e em Lisboa. Fazemos uma coisa e fazemos bem: massagem profunda que trabalha os músculos do rosto — sem agulhas, sem aparelhos invasivos, sem tempo de recuperação.',
  aboutWhoText2:
    'Terapeutas formadas em técnicas de lifting manual da Ásia e da Europa. Cada sessão é adaptada ao seu rosto — não há dois protocolos iguais.',
  aboutBook: 'Marcar agora — hoje ou outro dia',
  aboutQuestions: 'Tem dúvidas? Fale connosco',
  steps: ['Tratamento', 'Hora', 'Extras', 'Contacto', 'Confirmar'],
  pickDuration: 'Escolha a duração',
  next: 'Seguinte',
  anyTherapist: 'Qualquer terapeuta',
  morning: 'Manhã',
  afternoon: 'Tarde',
  evening: 'Fim do dia',
  noTimesDay: 'Sem horas livres neste dia. Escolha outro dia acima.',
  reschedule: 'Cancelamento: pedimos que nos avise pelo menos 6 horas antes, se algo mudar.',
  extrasTitle: 'Um mimo extra, só para si',
  extrasSub: 'Escolhido para o seu tratamento — junte se quiser.',
  forFace: 'Combina com tratamentos de rosto',
  forBody: 'Combina com massagem corporal',
  forSkin: 'Combina com tratamentos de pele',
  showAllExtras: (n: number) => `Ver todos os extras (${n})`,
  noExtras: 'Sem extras',
  // extras bar T1 + «Added» on a picked card (owner 31.10.2026)
  selected: 'Selecionado',
  added: 'Adicionado',
  treatMyself: 'Vou mimar-me hoje',
  yesAdd: (n: number, name: string, price: string) =>
    n === 1 ? `Sim, juntar ${name} · +${price}` : `Sim, juntar ${n} extras · +${price}`,
  changeMind: 'Mudei de ideias',
  noExtrasToday: 'Hoje não me apetece extras',
  giftForYou: 'Um presente para si',
  giftYours: 'O seu presente · oferta da casa',
  productsToggle: (n: number) => `Produtos para casa (${n})`,
  giftUnlocked: (gift: string) => `Presente incluído: ${gift}`,
  giftProgress: (missing: string) => `Faltam ${missing} para um presente da casa`,
  giftMore: (missing: string) => `Mais ${missing} e há mais presentes à escolha`,
  giftChoose: 'Escolha o seu presente',
  confirmTitle: 'Tudo certo? Vamos marcar',
  confirmBook: 'Confirmar marcação',
  edit: 'Alterar',
  findTitle: 'Encontrar a sua marcação',
  findSub: 'Escreva o telemóvel, ou passe o cartão de cliente no leitor.',
  findClassPass: 'Reservei pelo ClassPass',
  helloTitle: (name: string) => `Olá, ${name}!`,
  helloSub: 'É esta a sua visita de hoje?',
  imHere: 'Sim, cheguei',
  notMe: 'Não sou eu',
  remoteFindSub: 'Escreva o seu telemóvel para encontrar a marcação de hoje.',
  itsMe: 'Sim, sou eu',
  remoteNotFound: 'Não encontrámos uma marcação para hoje com este número. Verifique o número ou ligue-nos.',
  remoteDoneTitle: 'Está tudo pronto',
  remoteDoneText: (time: string) =>
    `Esperamos por si às ${time}. No estúdio, toque em «Tenho marcação» no ecrã de entrada — é só um toque.`,
  remoteChange: 'Alterar escolhas',
  offerTitle: 'Uma sugestão para hoje',
  offerAdd: (price: string) => `Adicionar · ${price}`,
  offerNo: 'Não, obrigada',
  payTitle: 'Como vai pagar no fim?',
  paySub: 'Paga à terapeuta depois do tratamento. Aqui não se cobra nada.',
  payLater: 'Decido no fim',
  doneArrived: (staff: string) => `${staff} já sabe que chegou. Sente-se, por favor — vem buscá-la em breve.`,
  doneArrivedNoStaff: 'A equipa já sabe que chegou. Sente-se, por favor.',
  doneBookedFuture: 'Até breve! No dia da visita, faça check-in neste ecrã.',
  closesIn: (s: number) => `Este ecrã fecha em ${s} s`,
  addToCalendar: 'Adicionar ao calendário',
  memIncluded: 'No seu abonamento',
  memShowAll: 'Mostrar todos os tratamentos',
  memOther: 'Fora do abonamento · pago à parte',
  memSession: '1 sessão do abonamento',
  memRow: 'Abonamento · 1 sessão',
  commentLabel: 'Comentário para a terapeuta (opcional)',
  commentHint: 'Ex.: pele sensível, chego 5 min mais tarde…',
  idleTitle: 'Ainda está aí?',
  idleSub: (s: number) => `Por privacidade, recomeçamos dentro de ${s} s.`,
  idleStay: 'Continuar',
  idleLeave: 'Recomeçar',
  giftValueTitle: 'Escolha o valor',
  giftHowShort: 'Como recebe',
};

export type V2Messages = typeof pt;

const en: V2Messages = {
  detailsTitle: 'Almost there — just a few details',
  isThisYou: (name: string) => `Is this you, ${name}?`,
  knownSub: 'We found your details by your mobile number.',
  knownEmailNeeded: 'We don’t have your e-mail yet. Please type it.',
  knownReady: 'Your details are ready',
  homeCheckin: 'I have a booking at 360°FACE',
  homeCheckinSub: 'Check in with 3 taps',
  homeBook: 'Book',
  homeBookSub: 'Today or another day',
  homeGift: 'Gift certificate + gift box',
  homeGiftSub: '40–150 €',
  homeAbout: 'Learn more about us',
  homeAboutSub: 'First time here? What we do, for whom, from how much · 1 min',
  aboutTitle: 'Face lifting without injections',
  aboutLead:
    'Deep massage of the face, neck and body. 30–65 minutes. You see the result as you leave, and it lasts — no needles, no downtime.',
  aboutHero: 'Facial massage — our speciality',
  aboutHeroSub: 'Sharper contour · less puffiness · glowing skin · a relaxed jaw',
  aboutSolves: 'What facial massage solves',
  aboutSolvesList: [
    'A softening jawline, double chin',
    'Puffiness and dark circles — lymphatic drainage',
    'Jaw tension, teeth grinding, headaches',
    'Expression lines on the forehead and around the eyes',
    'Dull, tired skin — it gets its glow back',
  ],
  aboutFace: 'Face',
  aboutFaceText: 'Face Workout, Korugi, Kobido, buccal. A natural lift that trains the facial muscles.',
  aboutBody: 'Body',
  aboutBodyText: 'Body massage that frees the back, legs and head. You leave lighter.',
  aboutSkin: 'Skin',
  aboutSkinText: 'Deep cleansing, peeling, LED, carboxytherapy. Clean, calm, luminous skin.',
  aboutFirst: 'First time?',
  aboutFirstText: 'Tell us what bothers you — the therapist picks the technique.',
  aboutTry: 'Try',
  aboutFrom: 'from',
  aboutStudios: 'studios · Parede and Lisbon',
  aboutMinutes: 'minutes per session',
  aboutZero: 'injections, needles, downtime',
  aboutWho: 'Who we are',
  aboutWhoText:
    '360°FACE is a manual face-lifting studio in Parede and Lisbon. We do one thing and do it well: deep massage that works the muscles of the face — no needles, no invasive devices, no downtime.',
  aboutWhoText2:
    'Therapists trained in manual lifting techniques from Asia and Europe. Every session is adapted to your face — no two protocols are the same.',
  aboutBook: 'Book now — today or another day',
  aboutQuestions: 'Any questions? Talk to us',
  steps: ['Treatment', 'Time', 'Extras', 'Contact', 'Confirm'],
  pickDuration: 'Choose the length',
  next: 'Next',
  anyTherapist: 'Any therapist',
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
  noTimesDay: 'No free times on this day. Pick another day above.',
  reschedule: 'Cancellation: please let us know at least 6 hours before if anything changes.',
  extrasTitle: 'A little extra, just for you',
  extrasSub: 'Picked to go with your treatment — add one if you like.',
  forFace: 'Goes with facial treatments',
  forBody: 'Goes with body massage',
  forSkin: 'Goes with skin treatments',
  showAllExtras: (n: number) => `See all extras (${n})`,
  noExtras: 'No extras',
  // extras bar T1 + «Added» on a picked card (owner 31.10.2026)
  selected: 'Selected',
  added: 'Added',
  treatMyself: 'Treat myself today',
  yesAdd: (n: number, name: string, price: string) =>
    n === 1 ? `Yes, add ${name} · +${price}` : `Yes, add ${n} extras · +${price}`,
  changeMind: 'Change my mind',
  noExtrasToday: 'I don’t feel like extras today',
  giftForYou: 'A gift for you',
  giftYours: 'Your gift · free from us',
  productsToggle: (n: number) => `Products for home (${n})`,
  giftUnlocked: (gift: string) => `Gift included: ${gift}`,
  giftProgress: (missing: string) => `${missing} more for a gift from us`,
  giftMore: (missing: string) => `${missing} more and there are more gifts to choose from`,
  giftChoose: 'Choose your gift',
  confirmTitle: 'All set? Let’s book it',
  confirmBook: 'Confirm booking',
  edit: 'Change',
  findTitle: 'Welcome back! Let’s find your booking',
  findSub: 'Type your mobile number, or scan your client card.',
  findClassPass: 'I booked with ClassPass',
  helloTitle: (name: string) => `Lovely to see you, ${name}!`,
  helloSub: 'Is this your visit today?',
  imHere: "Yes, I'm here",
  notMe: "That's not me",
  remoteFindSub: 'Type your mobile number to find today’s booking.',
  itsMe: "Yes, it's me",
  remoteNotFound: 'We could not find a booking for today with this number. Check the number or give us a call.',
  remoteDoneTitle: 'All set',
  remoteDoneText: (time: string) =>
    `We look forward to seeing you at ${time}. In the studio, tap “I have a booking” on the screen at the entrance — just one tap.`,
  remoteChange: 'Change my choices',
  offerTitle: 'One idea for today',
  offerAdd: (price: string) => `Add · ${price}`,
  offerNo: 'No, thank you',
  payTitle: 'How will you pay at the end?',
  paySub: 'You pay your therapist after the treatment. Nothing is charged here.',
  payLater: 'I will decide later',
  doneArrived: (staff: string) => `${staff} knows you are here. Please take a seat — she will come for you shortly.`,
  doneArrivedNoStaff: 'The team knows you are here. Please take a seat.',
  doneBookedFuture: 'See you soon! On the day, check in on this screen.',
  closesIn: (s: number) => `This screen closes in ${s} s`,
  addToCalendar: 'Add to calendar',
  memIncluded: 'In your membership',
  memShowAll: 'Show all treatments',
  memOther: 'Not in your membership · paid separately',
  memSession: '1 membership session',
  memRow: 'Membership · 1 session',
  commentLabel: 'Note for your therapist (optional)',
  commentHint: 'E.g. sensitive skin, running 5 min late…',
  idleTitle: 'Are you still there?',
  idleSub: (s: number) => `For your privacy we start over in ${s} s.`,
  idleStay: 'Continue',
  idleLeave: 'Start over',
  giftValueTitle: 'Choose the value',
  giftHowShort: 'How you receive it',
};

const fr: V2Messages = {
  ...en,
  addToCalendar: 'Ajouter au calendrier',
  memIncluded: 'Dans votre abonnement',
  memShowAll: 'Voir tous les soins',
  memOther: 'Hors abonnement · payé à part',
  memSession: '1 séance de l’abonnement',
  memRow: 'Abonnement · 1 séance',
  commentLabel: 'Commentaire pour la thérapeute (facultatif)',
  commentHint: 'Ex. : peau sensible, 5 min de retard…',
  detailsTitle: 'Veuillez indiquer vos coordonnées',
  isThisYou: (name: string) => `Est-ce vous, ${name} ?`,
  knownSub: 'Nous avons trouvé vos coordonnées grâce à votre numéro.',
  knownEmailNeeded: 'Nous n’avons pas encore votre e-mail. Veuillez l’écrire.',
  knownReady: 'Vos coordonnées sont prêtes',
  homeCheckin: "J'ai un rendez-vous chez 360°FACE",
  homeCheckinSub: 'Enregistrement en 3 gestes',
  homeBook: 'Réserver',
  homeBookSub: "Aujourd'hui ou un autre jour",
  homeGift: 'Bon cadeau + coffret cadeau',
  homeAbout: 'En savoir plus sur nous',
  homeAboutSub: 'Première visite ? Ce que nous faisons, pour qui, à partir de combien · 1 min',
  aboutTitle: 'Lifting du visage sans injections',
  aboutLead:
    'Massage profond du visage, du cou et du corps. 30–65 minutes. Le résultat se voit dès la sortie et dure — sans aiguilles, sans récupération.',
  aboutHero: 'Massage du visage — notre spécialité',
  aboutHeroSub: 'Contour plus net · moins de gonflement · peau lumineuse · mâchoire détendue',
  aboutSolves: 'Ce que résout le massage du visage',
  aboutSolvesList: [
    'Ovale qui s’affaisse, double menton',
    'Gonflements et cernes — drainage lymphatique',
    'Mâchoire tendue, bruxisme, maux de tête',
    'Rides d’expression du front et du contour des yeux',
    'Peau terne et fatiguée — elle retrouve son éclat',
  ],
  aboutFace: 'Visage',
  aboutFaceText: 'Face Workout, Korugi, Kobido, buccal. Un lifting naturel qui entraîne les muscles du visage.',
  aboutBody: 'Corps',
  aboutBodyText: 'Massage du corps qui libère le dos, les jambes et la tête. Vous repartez plus légère.',
  aboutSkin: 'Peau',
  aboutSkinText: 'Nettoyage profond, peeling, LED, carboxythérapie. Une peau nette, apaisée, lumineuse.',
  aboutFirst: 'Première fois ?',
  aboutFirstText: 'Dites-nous ce qui vous gêne — la thérapeute choisit la technique.',
  aboutTry: 'Essayez',
  aboutFrom: 'dès',
  aboutStudios: 'studios · Parede et Lisbonne',
  aboutMinutes: 'minutes par séance',
  aboutZero: 'injections, aiguilles, récupération',
  aboutWho: 'Qui nous sommes',
  aboutWhoText:
    '360°FACE est un studio de lifting manuel du visage, à Parede et à Lisbonne. Nous faisons une chose, et nous la faisons bien : un massage profond qui travaille les muscles du visage — sans aiguilles, sans appareils invasifs, sans récupération.',
  aboutWhoText2:
    'Des thérapeutes formées aux techniques de lifting manuel d’Asie et d’Europe. Chaque séance est adaptée à votre visage — il n’y a pas deux protocoles identiques.',
  aboutBook: 'Réserver — aujourd’hui ou un autre jour',
  aboutQuestions: 'Des questions ? Parlez-nous',
  steps: ['Soin', 'Heure', 'Options', 'Contact', 'Confirmer'],
  pickDuration: 'Choisissez la durée',
  next: 'Suivant',
  anyTherapist: 'Peu importe',
  morning: 'Matin',
  afternoon: 'Après-midi',
  evening: 'Soir',
  noTimesDay: "Pas d'horaire libre ce jour-là. Choisissez un autre jour.",
  reschedule: 'Annulation : merci de nous prévenir au moins 6 heures avant si quelque chose change.',
  extrasTitle: 'Complétez votre séance',
  extrasSub: 'Facultatif. Choisi pour votre soin.',
  noExtras: 'Sans option',
  // extras bar T1 + «Added» on a picked card (owner 31.10.2026)
  selected: 'Sélectionné',
  added: 'Ajouté',
  treatMyself: 'Je me fais plaisir aujourd’hui',
  yesAdd: (n: number, name: string, price: string) =>
    n === 1 ? `Oui, ajouter ${name} · +${price}` : `Oui, ajouter ${n} options · +${price}`,
  changeMind: 'J’ai changé d’avis',
  noExtrasToday: 'Pas d’option aujourd’hui',
  giftForYou: 'Un cadeau pour vous',
  giftYours: 'Votre cadeau · offert',
  productsToggle: (n: number) => `Produits pour la maison (${n})`,
  confirmTitle: 'Confirmez la réservation',
  confirmBook: 'Confirmer',
  edit: 'Modifier',
  findTitle: 'Retrouver votre rendez-vous',
  findSub: 'Saisissez votre mobile ou scannez votre carte client.',
  findClassPass: "J'ai réservé via ClassPass",
  helloTitle: (name: string) => `Bonjour, ${name} !`,
  helloSub: "C'est bien votre rendez-vous ?",
  imHere: 'Oui, je suis là',
  notMe: 'Ce n’est pas moi',
  remoteFindSub: 'Saisissez votre mobile pour trouver le rendez-vous d’aujourd’hui.',
  itsMe: 'Oui, c’est moi',
  remoteNotFound: 'Aucun rendez-vous aujourd’hui avec ce numéro. Vérifiez le numéro ou appelez-nous.',
  remoteDoneTitle: 'Tout est prêt',
  remoteDoneText: (time: string) =>
    `Nous vous attendons à ${time}. Au studio, touchez « J'ai un rendez-vous » sur l’écran à l’entrée — une seule touche.`,
  remoteChange: 'Modifier mes choix',
  offerNo: 'Non, merci',
  payTitle: 'Comment paierez-vous à la fin ?',
  paySub: 'Vous payez votre praticienne après le soin. Rien n’est débité ici.',
  payLater: 'Je déciderai plus tard',
  idleTitle: 'Vous êtes toujours là ?',
  idleStay: 'Continuer',
  idleLeave: 'Recommencer',
};

const es: V2Messages = {
  ...en,
  addToCalendar: 'Añadir al calendario',
  memIncluded: 'En su bono',
  memShowAll: 'Ver todos los tratamientos',
  memOther: 'Fuera del bono · se paga aparte',
  memSession: '1 sesión del bono',
  memRow: 'Bono · 1 sesión',
  commentLabel: 'Comentario para la terapeuta (opcional)',
  commentHint: 'Ej.: piel sensible, llego 5 min tarde…',
  detailsTitle: 'Por favor, rellene sus datos',
  isThisYou: (name: string) => `¿Es usted ${name}?`,
  knownSub: 'Hemos encontrado sus datos por su número de móvil.',
  knownEmailNeeded: 'Aún no tenemos su e-mail. Escríbalo, por favor.',
  knownReady: 'Sus datos están listos',
  homeCheckin: 'Tengo cita en 360°FACE',
  homeCheckinSub: 'Check-in en 3 toques',
  homeBook: 'Reservar',
  homeBookSub: 'Hoy u otro día',
  homeGift: 'Tarjeta regalo + caja de regalo',
  homeAbout: 'Saber más sobre nosotros',
  homeAboutSub: '¿Primera vez aquí? Qué hacemos, para quién, desde cuánto · 1 min',
  aboutTitle: 'Lifting facial sin inyecciones',
  aboutLead:
    'Masaje profundo de rostro, cuello y cuerpo. 30–65 minutos. El resultado se ve al salir y dura — sin agujas, sin recuperación.',
  aboutHero: 'Masaje facial — nuestra especialidad',
  aboutHeroSub: 'Contorno más definido · menos hinchazón · piel con luz · mandíbula relajada',
  aboutSolves: 'Lo que resuelve el masaje facial',
  aboutSolvesList: [
    'Óvalo que pierde definición, papada',
    'Hinchazón y ojeras — drenaje linfático',
    'Tensión mandibular, bruxismo, dolores de cabeza',
    'Líneas de expresión en frente y contorno de ojos',
    'Piel apagada y cansada — recupera la luz',
  ],
  aboutFace: 'Rostro',
  aboutFaceText: 'Face Workout, Korugi, Kobido, bucal. Lifting natural que entrena los músculos del rostro.',
  aboutBody: 'Cuerpo',
  aboutBodyText: 'Masaje corporal que libera la espalda, las piernas y la cabeza. Sales más ligera.',
  aboutSkin: 'Piel',
  aboutSkinText: 'Limpieza profunda, peeling, LED, carboxiterapia. Piel limpia, calmada, luminosa.',
  aboutFirst: '¿Primera vez?',
  aboutFirstText: 'Cuéntanos qué te molesta — la terapeuta elige la técnica.',
  aboutTry: 'Prueba',
  aboutFrom: 'desde',
  aboutStudios: 'estudios · Parede y Lisboa',
  aboutMinutes: 'minutos por sesión',
  aboutZero: 'inyecciones, agujas, recuperación',
  aboutWho: 'Quiénes somos',
  aboutWhoText:
    '360°FACE es un estudio de lifting facial manual, en Parede y en Lisboa. Hacemos una cosa y la hacemos bien: masaje profundo que trabaja los músculos del rostro — sin agujas, sin aparatos invasivos, sin recuperación.',
  aboutWhoText2:
    'Terapeutas formadas en técnicas de lifting manual de Asia y Europa. Cada sesión se adapta a tu rostro — no hay dos protocolos iguales.',
  aboutBook: 'Reservar ahora — hoy u otro día',
  aboutQuestions: '¿Dudas? Habla con nosotros',
  steps: ['Tratamiento', 'Hora', 'Extras', 'Contacto', 'Confirmar'],
  pickDuration: 'Elija la duración',
  next: 'Siguiente',
  anyTherapist: 'Cualquier terapeuta',
  morning: 'Mañana',
  afternoon: 'Tarde',
  evening: 'Última hora',
  noTimesDay: 'No hay horas libres este día. Elija otro día arriba.',
  reschedule: 'Cancelación: le pedimos que nos avise al menos 6 horas antes si algo cambia.',
  extrasTitle: 'Mejore su sesión',
  extrasSub: 'Opcional. Elegido para su tratamiento.',
  noExtras: 'Sin extras',
  // extras bar T1 + «Added» on a picked card (owner 31.10.2026)
  selected: 'Seleccionado',
  added: 'Añadido',
  treatMyself: 'Hoy me doy un capricho',
  yesAdd: (n: number, name: string, price: string) =>
    n === 1 ? `Sí, añadir ${name} · +${price}` : `Sí, añadir ${n} extras · +${price}`,
  changeMind: 'He cambiado de idea',
  noExtrasToday: 'Hoy no me apetecen extras',
  giftForYou: 'Un regalo para usted',
  giftYours: 'Su regalo · gratis',
  productsToggle: (n: number) => `Productos para casa (${n})`,
  confirmTitle: 'Confirme la reserva',
  confirmBook: 'Confirmar reserva',
  edit: 'Cambiar',
  findTitle: 'Buscar su cita',
  findSub: 'Escriba su móvil o pase su tarjeta de cliente.',
  findClassPass: 'Reservé con ClassPass',
  helloTitle: (name: string) => `¡Hola, ${name}!`,
  helloSub: '¿Es esta su cita de hoy?',
  imHere: 'Sí, ya llegué',
  notMe: 'No soy yo',
  remoteFindSub: 'Escriba su móvil para encontrar la cita de hoy.',
  itsMe: 'Sí, soy yo',
  remoteNotFound: 'No encontramos una cita para hoy con este número. Revise el número o llámenos.',
  remoteDoneTitle: 'Todo listo',
  remoteDoneText: (time: string) =>
    `Le esperamos a las ${time}. En el estudio, toque «Tengo cita» en la pantalla de la entrada — un solo toque.`,
  remoteChange: 'Cambiar mis opciones',
  offerNo: 'No, gracias',
  payTitle: '¿Cómo pagará al final?',
  paySub: 'Paga a su terapeuta después del tratamiento. Aquí no se cobra nada.',
  payLater: 'Lo decido al final',
  idleTitle: '¿Sigue ahí?',
  idleStay: 'Continuar',
  idleLeave: 'Empezar de nuevo',
};

const ru: V2Messages = {
  detailsTitle: 'Пожалуйста, заполните ваши данные',
  isThisYou: (name: string) => `Это вы, ${name}?`,
  knownSub: 'Мы нашли ваши данные по номеру телефона.',
  knownEmailNeeded: 'У нас пока нет вашего e-mail. Впишите его, пожалуйста.',
  knownReady: 'Ваши данные заполнены',
  homeCheckin: 'У меня запись в 360°FACE',
  homeCheckinSub: 'Отметиться за 3 касания',
  homeBook: 'Записаться',
  homeBookSub: 'Сегодня или в другой день',
  homeGift: 'Подарочный сертификат + подарочная коробка',
  homeGiftSub: '40–150 €',
  homeAbout: 'Узнать больше о нас',
  homeAboutSub: 'Впервые у нас? Что делаем, для кого, от какой цены · 1 мин',
  aboutTitle: 'Лифтинг лица без инъекций',
  aboutLead:
    'Глубокий массаж лица, шеи и тела. 30–65 минут. Результат виден сразу и держится — без игл и восстановления.',
  aboutHero: 'Массаж лица — наша специализация',
  aboutHeroSub: 'Чёткий контур · меньше отёков · сияющая кожа · расслабленная челюсть',
  aboutSolves: 'Что решает массаж лица',
  aboutSolvesList: [
    'Поплывший овал, второй подбородок',
    'Отёки и тёмные круги — лимфодренаж',
    'Зажатая челюсть, бруксизм, головные боли',
    'Мимические морщины на лбу и вокруг глаз',
    'Тусклая, уставшая кожа — возвращается сияние',
  ],
  aboutFace: 'Лицо',
  aboutFaceText: 'Face Workout, Коруги, Кобидо, буккальный. Естественный лифтинг, который тренирует мышцы лица.',
  aboutBody: 'Тело',
  aboutBodyText: 'Массаж тела: спина, ноги, голова. Выходите легче.',
  aboutSkin: 'Кожа',
  aboutSkinText: 'Глубокая чистка, пилинг, LED, карбокситерапия. Чистая, спокойная, сияющая кожа.',
  aboutFirst: 'Впервые?',
  aboutFirstText: 'Скажите, что беспокоит, — мастер подберёт технику.',
  aboutTry: 'Попробуйте',
  aboutFrom: 'от',
  aboutStudios: 'студии · Parede и Лиссабон',
  aboutMinutes: 'минут за сеанс',
  aboutZero: 'инъекций, игл, восстановления',
  aboutWho: 'Кто мы',
  aboutWhoText:
    '360°FACE — студия ручного лифтинга лица в Пареде и Лиссабоне. Мы делаем одно дело и делаем его хорошо: глубокий массаж, который работает с мышцами лица, — без игл, без инвазивных аппаратов, без восстановления.',
  aboutWhoText2:
    'Мастера, обученные техникам ручного лифтинга из Азии и Европы. Каждый сеанс подстраивается под ваше лицо — двух одинаковых протоколов не бывает.',
  aboutBook: 'Записаться — сегодня или в другой день',
  aboutQuestions: 'Есть вопросы? Позовите нас',
  steps: ['Процедура', 'Время', 'Дополнения', 'Контакты', 'Подтверждение'],
  pickDuration: 'Выберите длительность',
  next: 'Далее',
  anyTherapist: 'Любой мастер',
  morning: 'Утро',
  afternoon: 'День',
  evening: 'Вечер',
  noTimesDay: 'В этот день свободного времени нет. Выберите другой день выше.',
  reschedule: 'Отмена брони: просим уведомить не менее чем за 6 часов, если у вас что-то поменяется.',
  extrasTitle: 'Дополните процедуру',
  extrasSub: 'По желанию. Подобрано к вашей процедуре.',
  forFace: 'Подходит к процедурам для лица',
  forBody: 'Подходит к массажу тела',
  forSkin: 'Подходит к уходу за кожей',
  showAllExtras: (n: number) => `Все дополнения (${n})`,
  noExtras: 'Без дополнений',
  // extras bar T1 + «Added» on a picked card (owner 31.10.2026)
  selected: 'Выбрано',
  added: 'Добавлено',
  treatMyself: 'Побалую себя сегодня',
  yesAdd: (n: number, name: string, price: string) =>
    n === 1 ? `Да, добавить ${name} · +${price}` : `Да, добавить ${n} дополнения · +${price}`,
  changeMind: 'Передумал(а)',
  noExtrasToday: 'Сегодня без дополнений',
  giftForYou: 'Подарок для вас',
  giftYours: 'Ваш подарок · бесплатно',
  productsToggle: (n: number) => `Товары для ухода дома (${n})`,
  giftUnlocked: (gift: string) => `Подарок: ${gift}`,
  giftProgress: (missing: string) => `Ещё ${missing} — и подарок от студии`,
  giftMore: (missing: string) => `Ещё ${missing} — и выбор подарков больше`,
  giftChoose: 'Выберите подарок',
  confirmTitle: 'Проверьте запись',
  confirmBook: 'Подтвердить запись',
  edit: 'Изменить',
  findTitle: 'Найдём вашу запись',
  findSub: 'Введите номер телефона или поднесите карту клиента к сканеру.',
  findClassPass: 'Я записалась через ClassPass',
  helloTitle: (name: string) => `Здравствуйте, ${name}!`,
  helloSub: 'Это ваша запись на сегодня?',
  imHere: 'Да, я пришла',
  notMe: 'Это не я',
  remoteFindSub: 'Введите номер телефона, чтобы найти сегодняшнюю запись.',
  itsMe: 'Да, это я',
  remoteNotFound: 'На этот номер нет записи на сегодня. Проверьте номер или позвоните нам.',
  remoteDoneTitle: 'Всё готово',
  remoteDoneText: (time: string) =>
    `Ждём вас в ${time}. В студии нажмите «У меня запись» на экране у входа — одно касание.`,
  remoteChange: 'Изменить выбор',
  offerTitle: 'Идея на сегодня',
  offerAdd: (price: string) => `Добавить · ${price}`,
  offerNo: 'Нет, спасибо',
  payTitle: 'Как будете платить в конце?',
  paySub: 'Оплата мастеру после процедуры. Здесь ничего не списывается.',
  payLater: 'Решу в конце',
  doneArrived: (staff: string) => `${staff} знает, что вы пришли. Присаживайтесь — она скоро подойдёт.`,
  doneArrivedNoStaff: 'Команда знает, что вы пришли. Присаживайтесь, пожалуйста.',
  doneBookedFuture: 'До встречи! В день визита отметьтесь на этом экране.',
  closesIn: (s: number) => `Экран закроется через ${s} с`,
  addToCalendar: 'Добавить в календарь',
  memIncluded: 'Входит в ваш абонемент',
  memShowAll: 'Показать все процедуры',
  memOther: 'Не входит в абонемент · оплачивается отдельно',
  memSession: '1 сеанс абонемента',
  memRow: 'Абонемент · 1 сеанс',
  commentLabel: 'Комментарий для мастера (необязательно)',
  commentHint: 'Например: чувствительная кожа, опоздаю на 5 минут…',
  idleTitle: 'Вы ещё здесь?',
  idleSub: (s: number) => `Ради вашей приватности начнём заново через ${s} с.`,
  idleStay: 'Продолжить',
  idleLeave: 'Начать заново',
  giftValueTitle: 'Выберите номинал',
  giftHowShort: 'Как получить',
};

const uk: V2Messages = {
  ...ru,
  addToCalendar: 'Додати в календар',
  memIncluded: 'Входить у ваш абонемент',
  memShowAll: 'Показати всі процедури',
  memOther: 'Не входить в абонемент · оплачується окремо',
  memSession: '1 сеанс абонемента',
  memRow: 'Абонемент · 1 сеанс',
  commentLabel: 'Коментар для майстра (необовʼязково)',
  commentHint: 'Наприклад: чутлива шкіра, запізнюся на 5 хвилин…',
  detailsTitle: 'Будь ласка, заповніть ваші дані',
  isThisYou: (name: string) => `Це ви, ${name}?`,
  knownSub: 'Ми знайшли ваші дані за номером телефону.',
  knownEmailNeeded: 'У нас ще немає вашого e-mail. Впишіть його, будь ласка.',
  knownReady: 'Ваші дані заповнено',
  homeCheckin: 'У мене запис у 360°FACE',
  homeCheckinSub: 'Відмітитися за 3 дотики',
  homeBook: 'Записатися',
  homeBookSub: 'Сьогодні або іншого дня',
  homeGift: 'Подарунковий сертифікат + подарункова коробка',
  homeAbout: 'Дізнатися більше про нас',
  homeAboutSub: 'Уперше в нас? Що робимо, для кого, від якої ціни · 1 хв',
  aboutTitle: 'Ліфтинг обличчя без ін’єкцій',
  aboutLead:
    'Глибокий масаж обличчя, шиї та тіла. 30–65 хвилин. Результат видно одразу, і він тримається — без голок і відновлення.',
  aboutHero: 'Масаж обличчя — наша спеціалізація',
  aboutHeroSub: 'Чіткий контур · менше набряків · сяюча шкіра · розслаблена щелепа',
  aboutSolves: 'Що вирішує масаж обличчя',
  aboutSolvesList: [
    'Поплилий овал, друге підборіддя',
    'Набряки й темні кола — лімфодренаж',
    'Затиснута щелепа, бруксизм, головний біль',
    'Мімічні зморшки на чолі та навколо очей',
    'Тьмяна, втомлена шкіра — повертається сяйво',
  ],
  aboutFace: 'Обличчя',
  aboutFaceText: 'Face Workout, Коругі, Кобідо, букальний. Природний ліфтинг, що тренує м’язи обличчя.',
  aboutBody: 'Тіло',
  aboutBodyText: 'Масаж тіла: спина, ноги, голова. Виходите легшою.',
  aboutSkin: 'Шкіра',
  aboutSkinText: 'Глибоке очищення, пілінг, LED, карбокситерапія. Чиста, спокійна, сяюча шкіра.',
  aboutFirst: 'Уперше?',
  aboutFirstText: 'Скажіть, що турбує, — майстер підбере техніку.',
  aboutTry: 'Спробуйте',
  aboutFrom: 'від',
  aboutStudios: 'студії · Parede і Лісабон',
  aboutMinutes: 'хвилин за сеанс',
  aboutZero: 'ін’єкцій, голок, відновлення',
  aboutWho: 'Хто ми',
  aboutWhoText:
    '360°FACE — студія ручного ліфтингу обличчя в Пареде та Лісабоні. Ми робимо одну справу і робимо її добре: глибокий масаж, що працює з м’язами обличчя, — без голок, без інвазивних апаратів, без відновлення.',
  aboutWhoText2:
    'Майстри, навчені технікам ручного ліфтингу з Азії та Європи. Кожен сеанс підлаштовується під ваше обличчя — двох однакових протоколів не буває.',
  aboutBook: 'Записатися — сьогодні або іншого дня',
  aboutQuestions: 'Є запитання? Покличте нас',
  steps: ['Процедура', 'Час', 'Доповнення', 'Контакти', 'Підтвердження'],
  pickDuration: 'Оберіть тривалість',
  next: 'Далі',
  anyTherapist: 'Будь-який майстер',
  morning: 'Ранок',
  afternoon: 'День',
  evening: 'Вечір',
  noTimesDay: 'Цього дня вільного часу немає. Оберіть інший день вище.',
  reschedule: 'Скасування броні: просимо повідомити не менш ніж за 6 годин, якщо у вас щось зміниться.',
  extrasTitle: 'Доповніть процедуру',
  extrasSub: 'За бажанням. Підібрано до вашої процедури.',
  noExtras: 'Без доповнень',
  // extras bar T1 + «Added» on a picked card (owner 31.10.2026)
  selected: 'Обрано',
  added: 'Додано',
  treatMyself: 'Побалую себе сьогодні',
  yesAdd: (n: number, name: string, price: string) =>
    n === 1 ? `Так, додати ${name} · +${price}` : `Так, додати ${n} доповнення · +${price}`,
  changeMind: 'Передумав(ла)',
  noExtrasToday: 'Сьогодні без доповнень',
  giftForYou: 'Подарунок для вас',
  giftYours: 'Ваш подарунок · безкоштовно',
  productsToggle: (n: number) => `Товари для догляду вдома (${n})`,
  confirmTitle: 'Перевірте запис',
  confirmBook: 'Підтвердити запис',
  edit: 'Змінити',
  findTitle: 'Знайдемо ваш запис',
  findSub: 'Введіть номер телефону або піднесіть картку клієнта до сканера.',
  findClassPass: 'Я записалася через ClassPass',
  helloTitle: (name: string) => `Вітаємо, ${name}!`,
  helloSub: 'Це ваш запис на сьогодні?',
  imHere: 'Так, я прийшла',
  notMe: 'Це не я',
  remoteFindSub: 'Введіть номер телефону, щоб знайти сьогоднішній запис.',
  itsMe: 'Так, це я',
  remoteNotFound: 'На цей номер немає запису на сьогодні. Перевірте номер або зателефонуйте нам.',
  remoteDoneTitle: 'Усе готово',
  remoteDoneText: (time: string) =>
    `Чекаємо на вас о ${time}. У студії натисніть «У мене запис» на екрані біля входу — один дотик.`,
  remoteChange: 'Змінити вибір',
  offerTitle: 'Ідея на сьогодні',
  offerAdd: (price: string) => `Додати · ${price}`,
  offerNo: 'Ні, дякую',
  payTitle: 'Як будете платити наприкінці?',
  paySub: 'Оплата майстру після процедури. Тут нічого не списується.',
  payLater: 'Вирішу наприкінці',
  idleTitle: 'Ви ще тут?',
  idleStay: 'Продовжити',
  idleLeave: 'Почати знову',
};

const de: V2Messages = {
  ...en,
  detailsTitle: 'Bitte ergänzen Sie Ihre Angaben',
  isThisYou: (name: string) => `Sind Sie ${name}?`,
  knownSub: 'Wir haben Ihre Angaben über Ihre Handynummer gefunden.',
  knownEmailNeeded: 'Wir haben Ihre E-Mail-Adresse noch nicht. Bitte geben Sie sie ein.',
  knownReady: 'Ihre Angaben sind vollständig',
  homeCheckin: 'Ich habe einen Termin bei 360°FACE',
  homeCheckinSub: 'Einchecken mit 3 Klicks',
  homeBook: 'Buchen',
  homeBookSub: 'Heute oder an einem anderen Tag',
  homeGift: 'Geschenkgutschein + Geschenkbox',
  homeGiftSub: '40–150 €',
  homeAbout: 'Mehr über uns erfahren',
  homeAboutSub: 'Zum ersten Mal hier? Was wir tun, für wen, ab wie viel · 1 Min.',
  aboutTitle: 'Gesichtslifting ohne Injektionen',
  aboutLead:
    'Tiefe Massage von Gesicht, Hals und Körper. 30–65 Minuten. Das Ergebnis sieht man sofort, und es hält — keine Nadeln, keine Ausfallzeit.',
  aboutHero: 'Gesichtsmassage — unsere Spezialität',
  aboutHeroSub: 'Klarere Kontur · weniger Schwellung · strahlende Haut · entspannter Kiefer',
  aboutSolves: 'Was die Gesichtsmassage löst',
  aboutSolvesList: [
    'Nachlassende Gesichtskontur, Doppelkinn',
    'Schwellungen und Augenringe — Lymphdrainage',
    'Kieferverspannung, Zähneknirschen, Kopfschmerzen',
    'Mimikfalten auf Stirn und um die Augen',
    'Fahle, müde Haut — sie bekommt ihren Glanz zurück',
  ],
  aboutFace: 'Gesicht',
  aboutFaceText: 'Face Workout, Korugi, Kobido, bukkal. Natürliches Lifting, das die Gesichtsmuskeln trainiert.',
  aboutBody: 'Körper',
  aboutBodyText: 'Körpermassage, die Rücken, Beine und Kopf löst. Sie gehen leichter hinaus.',
  aboutSkin: 'Haut',
  aboutSkinText: 'Tiefenreinigung, Peeling, LED, Carboxytherapie. Reine, ruhige, strahlende Haut.',
  aboutFirst: 'Zum ersten Mal?',
  aboutFirstText: 'Sagen Sie uns, was Sie stört — die Therapeutin wählt die Technik.',
  aboutTry: 'Probieren Sie',
  aboutFrom: 'ab',
  aboutStudios: 'Studios · Parede und Lissabon',
  aboutMinutes: 'Minuten pro Sitzung',
  aboutZero: 'Injektionen, Nadeln, Ausfallzeit',
  aboutWho: 'Wer wir sind',
  aboutWhoText:
    '360°FACE ist ein Studio für manuelles Gesichtslifting in Parede und Lissabon. Wir tun eine Sache und tun sie gut: tiefe Massage, die die Gesichtsmuskeln trainiert — ohne Nadeln, ohne invasive Geräte, ohne Ausfallzeit.',
  aboutWhoText2:
    'Therapeutinnen, ausgebildet in manuellen Lifting-Techniken aus Asien und Europa. Jede Sitzung wird an Ihr Gesicht angepasst — kein Protokoll gleicht dem anderen.',
  aboutBook: 'Jetzt buchen — heute oder an einem anderen Tag',
  aboutQuestions: 'Fragen? Sprechen Sie uns an',
  steps: ['Behandlung', 'Uhrzeit', 'Extras', 'Kontakt', 'Bestätigen'],
  pickDuration: 'Dauer wählen',
  next: 'Weiter',
  anyTherapist: 'Beliebige Therapeutin',
  morning: 'Vormittag',
  afternoon: 'Nachmittag',
  evening: 'Abend',
  noTimesDay: 'An diesem Tag sind keine Zeiten frei. Wählen Sie oben einen anderen Tag.',
  reschedule: 'Stornierung: Bitte geben Sie uns mindestens 6 Stunden vorher Bescheid, falls sich etwas ändert.',
  extrasTitle: 'Bereichern Sie Ihre Sitzung',
  extrasSub: 'Optional. Passend zu Ihrer Behandlung ausgewählt.',
  forFace: 'Passt zu Gesichtsbehandlungen',
  forBody: 'Passt zur Körpermassage',
  forSkin: 'Passt zu Hautbehandlungen',
  showAllExtras: (n: number) => `Alle Extras anzeigen (${n})`,
  noExtras: 'Ohne Extras',
  // extras bar T1 + «Added» on a picked card (owner 31.10.2026)
  selected: 'Ausgewählt',
  added: 'Hinzugefügt',
  treatMyself: 'Heute gönne ich mir etwas',
  yesAdd: (n: number, name: string, price: string) =>
    n === 1 ? `Ja, ${name} dazu · +${price}` : `Ja, ${n} Extras dazu · +${price}`,
  changeMind: 'Doch nicht',
  noExtrasToday: 'Heute keine Extras',
  giftForYou: 'Ein Geschenk für Sie',
  giftYours: 'Ihr Geschenk · gratis',
  productsToggle: (n: number) => `Produkte für zu Hause (${n})`,
  giftUnlocked: (gift: string) => `Geschenk inklusive: ${gift}`,
  giftProgress: (missing: string) => `Noch ${missing} bis zu einem Geschenk des Hauses`,
  giftMore: (missing: string) => `Noch ${missing} und Sie haben mehr Geschenke zur Auswahl`,
  giftChoose: 'Wählen Sie Ihr Geschenk',
  confirmTitle: 'Termin bestätigen',
  confirmBook: 'Termin bestätigen',
  edit: 'Ändern',
  findTitle: 'Ihren Termin finden',
  findSub: 'Geben Sie Ihre Handynummer ein oder scannen Sie Ihre Kundenkarte.',
  findClassPass: 'Ich habe über ClassPass gebucht',
  helloTitle: (name: string) => `Hallo, ${name}!`,
  helloSub: 'Ist das Ihr heutiger Termin?',
  imHere: 'Ja, ich bin da',
  notMe: 'Das bin ich nicht',
  remoteFindSub: 'Geben Sie Ihre Handynummer ein, um Ihren heutigen Termin zu finden.',
  itsMe: 'Ja, das bin ich',
  remoteNotFound:
    'Für diese Nummer haben wir heute keinen Termin gefunden. Prüfen Sie die Nummer oder rufen Sie uns an.',
  remoteDoneTitle: 'Alles bereit',
  remoteDoneText: (time: string) =>
    `Wir freuen uns auf Sie um ${time}. Tippen Sie im Studio am Bildschirm beim Eingang auf „Ich habe einen Termin“ – ein Tipp genügt.`,
  remoteChange: 'Auswahl ändern',
  offerTitle: 'Eine Idee für heute',
  offerAdd: (price: string) => `Hinzufügen · ${price}`,
  offerNo: 'Nein, danke',
  payTitle: 'Wie möchten Sie am Ende bezahlen?',
  paySub: 'Sie bezahlen nach der Behandlung bei Ihrer Therapeutin. Hier wird nichts berechnet.',
  payLater: 'Ich entscheide später',
  doneArrived: (staff: string) => `${staff} weiß, dass Sie da sind. Bitte nehmen Sie Platz – sie holt Sie in Kürze ab.`,
  doneArrivedNoStaff: 'Unser Team weiß, dass Sie da sind. Bitte nehmen Sie Platz.',
  doneBookedFuture: 'Bis bald! Checken Sie am Tag Ihres Besuchs an diesem Bildschirm ein.',
  closesIn: (s: number) => `Dieser Bildschirm schließt in ${s} s`,
  addToCalendar: 'Zum Kalender hinzufügen',
  memIncluded: 'In Ihrem Abo',
  memShowAll: 'Alle Behandlungen zeigen',
  memOther: 'Nicht im Abo · separat bezahlt',
  memSession: '1 Abo-Sitzung',
  memRow: 'Abo · 1 Sitzung',
  commentLabel: 'Hinweis für die Therapeutin (optional)',
  commentHint: 'Z. B. empfindliche Haut, 5 Min. später…',
  idleTitle: 'Sind Sie noch da?',
  idleSub: (s: number) => `Zum Schutz Ihrer Privatsphäre beginnen wir in ${s} s von vorn.`,
  idleStay: 'Weiter',
  idleLeave: 'Neu beginnen',
  giftValueTitle: 'Betrag wählen',
  giftHowShort: 'Wie Sie ihn erhalten',
};

export const V2_MESSAGES: Record<Locale, V2Messages> = { pt, en, fr, es, ru, uk, de };
